import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, LogOut, Upload, X, Image as ImageIcon } from "lucide-react";
import PackageBuilder from "@/components/PackageBuilder";
import ScheduleBuilder from "@/components/ScheduleBuilder";
import FaqBuilder from "@/components/FaqBuilder";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEvents() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/events`, { headers: getHeaders() });
      setEvents(res.data);
    } catch (err) { devWarn("Failed to load events:", err.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchEvents(); }, [user]);

  const toggleVisibility = async (slug, visible) => {
    await axios.put(`${API}/admin/events/${slug}`, { visible: !visible }, { headers: getHeaders() });
    toast.success(visible ? "Event dolt" : "Event synligt");
    fetchEvents();
  };

  const deleteEvent = async (slug) => {
    if (!window.confirm("Radera detta event? Detta går inte att ångra.")) return;
    await axios.delete(`${API}/admin/events/${slug}`, { headers: getHeaders() });
    toast.success("Event raderat");
    fetchEvents();
  };

  const moveEvent = async (index, direction) => {
    const newEvents = [...events];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newEvents.length) return;
    [newEvents[index], newEvents[swapIndex]] = [newEvents[swapIndex], newEvents[index]];
    const order = newEvents.map(e => e.slug);
    await axios.put(`${API}/admin/events/reorder`, { order }, { headers: getHeaders() });
    fetchEvents();
  };

  const emptyForm = { slug: "", title: "", subtitle: "", location: "", date: "", date_details: "", starting_price: 0, currency: "SEK", price_label: "ex moms", description: "", long_description: "", image_url: "", image_fit: "cover", tags: "", availability: "Available", visible: true, packages: [], whats_included_text: "", schedule: [], faq: [] };

  const [form, setForm] = useState(emptyForm);

  const openEdit = (event) => {
    const genKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setForm({
      ...event,
      tags: (event.tags || []).join(", "),
      packages: (event.packages || []).map(p => ({
        ...p,
        items: p.items || [""],
        _key: p._key || genKey("p"),
        _itemKeys: (p.items || [""]).map(() => genKey("i")),
      })),
      whats_included_text: (event.whats_included || []).join("\n"),
      schedule: (event.schedule || []).map(s => ({ ...s, _key: s._key || genKey("s") })),
      faq: (event.faq || []).map(f => ({ ...f, _key: f._key || genKey("f") })),
    });
    setEditing(event.slug);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditing(null);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Bild uppladdad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
    }
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      starting_price: parseFloat(form.starting_price) || 0,
      whats_included: form.whats_included_text ? form.whats_included_text.split("\n").map(s => s.trim()).filter(Boolean) : [],
      packages: (form.packages || []).map(({ _key, _itemKeys, ...p }) => ({
        ...p,
        price: parseFloat(p.price) || 0,
        items: (p.items || []).filter(item => item.trim()),
      })),
      schedule: (form.schedule || []).map(({ _key, ...s }) => s),
      faq: (form.faq || []).map(({ _key, ...f }) => f),
    };
    delete payload.whats_included_text;
    delete payload._id;
    delete payload.created_at;
    try {
      if (editing) {
        await axios.put(`${API}/admin/events/${editing}`, payload, { headers: getHeaders() });
        toast.success("Event uppdaterat");
      } else {
        payload.sort_order = events.length;
        await axios.post(`${API}/admin/events`, payload, { headers: getHeaders() });
        toast.success("Event skapat");
      }
      setShowForm(false);
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte spara"); }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-4 py-2.5 text-white font-['Outfit'] text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";
  const labelCls = "text-white/60 text-xs font-['Outfit'] uppercase tracking-wider block mb-1.5";
  const helpCls = "text-white/30 text-[11px] font-['Outfit'] mt-1";

  let pageTitle = "Hantera events";
  if (showForm && editing) pageTitle = "Redigera event";
  else if (showForm) pageTitle = "Nytt event";

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-events-page">
      <nav className="bg-[#141414] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA ADMIN</Link>
            <div className="hidden md:flex gap-6">
              <Link to="/admin" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Översikt</Link>
              <Link to="/admin/events" className="text-white text-xs tracking-wider uppercase font-['Outfit']">Events</Link>
              <Link to="/admin/pages" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Sidor</Link>
              <Link to="/admin/orders" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Tävling</Link>
              <Link to="/admin/settings" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Inställningar</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/40 hover:text-white text-xs font-['Outfit']">Visa sajten</Link>
            <AdminThemeToggle />
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-white/40 hover:text-red-400"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-['Playfair_Display'] text-3xl text-white">{pageTitle}</h1>
          <div className="flex gap-3">
            {showForm && editing && (
              <a href={`/event/${form.slug}`} target="_blank" rel="noopener noreferrer" data-testid="preview-event-btn" className="btn-secondary px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2">
                <Eye size={14} /> Förhandsvisa event
              </a>
            )}
            {!showForm && <button onClick={openNew} data-testid="add-event-btn" className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><Plus size={14} /> Lägg till event</button>}
            {showForm && <button onClick={() => setShowForm(false)} className="btn-secondary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><X size={14} /> Avbryt</button>}
          </div>
        </div>

        {/* ──── EVENT FORM ──── */}
        {showForm && (
          <form onSubmit={saveEvent} className="space-y-6 mb-12">

            {/* INTRO / VÄGLEDNING */}
            <div className="border border-[#C5A059]/30 bg-[#C5A059]/5 p-5">
              <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase font-['Outfit'] font-semibold mb-2">Så fungerar formuläret</p>
              <p className="text-white/60 font-['Outfit'] text-sm leading-relaxed">
                Fyll i stegen 1–8 nedan. Varje steg har en liten förklaring som visar <span className="text-[#C5A059]">var innehållet hamnar på publika sidan</span>. Stjärnmarkerade (*) fält är obligatoriska. Du kan när som helst klicka <span className="text-white">"Förhandsvisa event"</span> uppe till höger för att se resultatet i en ny flik.
              </p>
            </div>

            {/* 1. BASIC INFO */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 1</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Grundinfo</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5 ml-0">Visas <span className="text-white/70">överst på event-kortet på startsidan</span> och <span className="text-white/70">i headern på detaljsidan</span>.</p>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Event-titel *</label>
                    <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="t.ex. Manchester United" required className={inputCls} />
                    <p className={helpCls}>Huvudrubriken som visas på event-kort och detaljsida</p>
                  </div>
                  <div>
                    <label className={labelCls}>Underrubrik</label>
                    <input value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} placeholder="t.ex. Stockholm — Vänskapsmatch" className={inputCls} />
                    <p className={helpCls}>Visas under titeln i guldtext</p>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>URL-slug *</label>
                  <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} placeholder="t.ex. manchester-united-stockholm" required disabled={!!editing} className={`${inputCls} ${editing ? 'opacity-50' : ''}`} />
                  <p className={helpCls}>Del av webbadressen: seravasports.com/event/<strong>{form.slug || 'din-slug'}</strong>. Går inte att ändra efter skapande.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Plats *</label>
                    <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="t.ex. Stockholm, Sverige" required className={inputCls} />
                    <p className={helpCls}>Visas bredvid datum på kort och detaljsida</p>
                  </div>
                  <div>
                    <label className={labelCls}>Datum *</label>
                    <input value={form.date} onChange={e => setForm({...form, date: e.target.value})} placeholder="t.ex. 1 augusti 2026" required className={inputCls} />
                    <p className={helpCls}>Visas som text — skriv precis så du vill att det ska synas på sajten</p>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Tidsinfo (valfritt)</label>
                  <input value={form.date_details || ""} onChange={e => setForm({...form, date_details: e.target.value})} placeholder="t.ex. Dörrar öppnar 14:00 | Event 16:00–19:00 | Afterparty 19:00–00:00" className={inputCls} />
                  <p className={helpCls}>Extra info om tider, visas på detaljsidan under datum</p>
                </div>
              </div>
            </div>

            {/* 2. IMAGE */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 2</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Eventbild</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Huvudbilden visas <span className="text-white/70">på event-kortet på startsidan</span> och som <span className="text-white/70">banner överst på detaljsidan</span>. Viktigt: använd <span className="text-amber-400">.jpg eller .png</span> — Stripes checkout accepterar inte .webp eller .svg.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Ladda upp bild</label>
                  <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-image-btn"
                    className="w-full flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-[#262626] hover:border-[#C5A059]/40 text-white/40 hover:text-white/60 transition-colors cursor-pointer">
                    {uploading ? <span className="animate-spin w-5 h-5 border-2 border-[#C5A059] border-t-transparent rounded-full" /> : <Upload size={20} />}
                    <span className="text-sm font-['Outfit']">{uploading ? "Laddar upp..." : "Klicka för att ladda upp bild"}</span>
                  </button>
                  <p className={helpCls}>JPG eller PNG. Max 10 MB.</p>

                  <div className="mt-4">
                    <label className={labelCls}>Eller klistra in bild-URL</label>
                    <input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className={inputCls} />
                  </div>

                  <div className="mt-4">
                    <label className={labelCls}>Bildformat</label>
                    <select value={form.image_fit} onChange={e => setForm({...form, image_fit: e.target.value})} className={inputCls}>
                      <option value="cover">Täcker hela kortet (beskärs vid kanterna)</option>
                      <option value="contain">Hela bilden syns (bra för logotyper)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Förhandsvisning</label>
                  {form.image_url ? (
                    <div className={`border border-[#262626] h-48 overflow-hidden ${form.image_fit === 'contain' ? 'bg-black' : ''}`}>
                      <img src={form.image_url} alt="Förhandsvisning" className={`w-full h-full ${form.image_fit === 'contain' ? 'object-contain p-4' : 'object-cover'}`} />
                    </div>
                  ) : (
                    <div className="border border-[#262626] h-48 flex items-center justify-center bg-[#1A1A1A]">
                      <ImageIcon size={32} className="text-white/15" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 3. DESCRIPTION */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 3</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Beskrivning</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Kort text på <span className="text-white/70">event-kortet</span>, längre text på <span className="text-white/70">detaljsidan</span>.</p>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Kort beskrivning *</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Kort sammanfattning som visas på event-kortet (1–2 meningar)" rows={2} required className={inputCls} />
                  <p className={helpCls}>Visas på event-kortet i events-rutnätet på startsidan</p>
                </div>
                <div>
                  <label className={labelCls}>Fullständig beskrivning</label>
                  <textarea value={form.long_description || ""} onChange={e => setForm({...form, long_description: e.target.value})} placeholder="Detaljerad beskrivning för detaljsidan" rows={4} className={inputCls} />
                  <p className={helpCls}>Visas på detaljsidan under rubriken "Om eventet". Lämna tom för att använda korta beskrivningen.</p>
                </div>
              </div>
            </div>

            {/* 4. PRICING & STATUS */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 4</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Pris & Status</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5"><span className="text-white/70">"Från-pris"</span> och <span className="text-white/70">tillgänglighets-badge</span> visas på event-kortet. Taggar visas som små etiketter på kortet.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Från-pris (SEK)</label>
                  <input value={form.starting_price} onChange={e => setForm({...form, starting_price: e.target.value})} type="number" step="0.01" placeholder="0" className={inputCls} />
                  <p className={helpCls}>Visas som "Från X SEK" på kortet. Sätt till 0 för "Kommer snart"-event</p>
                </div>
                <div>
                  <label className={labelCls}>Tillgänglighet</label>
                  <select value={form.availability} onChange={e => setForm({...form, availability: e.target.value})} className={inputCls}>
                    <option value="Available">Tillgängligt</option>
                    <option value="Limited">Begränsat</option>
                    <option value="Sold Out">Slutsålt</option>
                    <option value="Coming Soon">Kommer snart</option>
                  </select>
                  <p className={helpCls}>Visas som badge uppe till höger på event-kortet</p>
                </div>
                <div>
                  <label className={labelCls}>Taggar</label>
                  <input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="VIP, Hospitality, Corporate" className={inputCls} />
                  <p className={helpCls}>Kommaseparerade. Visas som etiketter på event-kortet.</p>
                </div>
              </div>
            </div>

            {/* 5. WHAT'S INCLUDED */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 5</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Vad som ingår</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Generell lista som gäller hela eventet. Visas som <span className="text-white/70">bockade rader</span> på detaljsidan. <span className="text-white/50">(Paketspecifika rader läggs till i Steg 6.)</span></p>
              <label className={labelCls}>Rader (en per rad)</label>
              <textarea value={form.whats_included_text} onChange={e => setForm({...form, whats_included_text: e.target.value})} placeholder={"Premiumplats\nVIP-lounge-tillgång\nMat & dryck\nDedikerad concierge"} rows={5} className={inputCls} />
              <p className={helpCls}>Varje rad blir en bockad punkt i sektionen "Vad som ingår" på detaljsidan</p>
            </div>

            {/* 6. PACKAGES */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 6</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Paket (köp-varianter)</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Varje paket blir ett <span className="text-white/70">köpbart kort</span> på detaljsidan med egen "Köp nu" / "Begär faktura"-knapp. Klicka på ⭐ för att markera ett paket som <span className="text-[#C5A059]">"Rekommenderat"</span>.</p>
              <PackageBuilder packages={form.packages || []} onChange={(pkgs) => setForm({ ...form, packages: pkgs })} />
            </div>

            {/* 7. SCHEDULE */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 7</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Schema (valfritt)</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Tidpunkter för event-dagen. Visas som en <span className="text-white/70">tidslinje</span> på detaljsidan.</p>
              <ScheduleBuilder items={form.schedule || []} onChange={(s) => setForm({ ...form, schedule: s })} />
            </div>

            {/* 8. FAQ */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 8</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Vanliga frågor (valfritt)</h3>
              </div>
              <p className="text-white/40 text-xs font-['Outfit'] mb-5">Frågor & svar. Visas som <span className="text-white/70">expanderbar lista</span> längst ner på detaljsidan.</p>
              <FaqBuilder items={form.faq || []} onChange={(f) => setForm({ ...form, faq: f })} />
            </div>

            {/* SAVE */}
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="save-event-btn" className="btn-primary px-8 py-3.5 text-xs tracking-[0.15em] uppercase font-['Outfit']">
                {editing ? "Spara ändringar" : "Skapa event"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary px-8 py-3.5 text-xs tracking-[0.15em] uppercase font-['Outfit']">Avbryt</button>
            </div>
          </form>
        )}

        {/* ──── EVENTS LIST ──── */}
        {!showForm && (
          <div className="space-y-2">
            {events.map((event, i) => (
              <div key={event.slug} className={`flex items-center gap-4 p-4 bg-[#141414] border ${event.visible ? 'border-[#262626]' : 'border-[#262626]/50 opacity-60'}`}>
                <div className="flex flex-col gap-1">
                  <button onClick={() => moveEvent(i, -1)} disabled={i === 0} className="text-white/30 hover:text-white disabled:opacity-20"><ArrowUp size={14} /></button>
                  <button onClick={() => moveEvent(i, 1)} disabled={i === events.length - 1} className="text-white/30 hover:text-white disabled:opacity-20"><ArrowDown size={14} /></button>
                </div>
                <img src={event.image_url} alt="" className="w-16 h-12 object-cover bg-black flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-['Outfit'] font-medium text-sm truncate">{event.title} {event.subtitle && <span className="text-white/40">— {event.subtitle}</span>}</p>
                  <p className="text-white/40 font-['Outfit'] text-xs">{event.date} · {event.location}</p>
                </div>
                <span className="text-white/50 text-xs font-['Outfit'] hidden sm:block">{event.starting_price > 0 ? `${event.starting_price.toLocaleString()} SEK` : "—"}</span>
                <div className="flex gap-2">
                  <button onClick={() => toggleVisibility(event.slug, event.visible)} className="p-2 text-white/30 hover:text-white transition-colors" title={event.visible ? "Dölj" : "Visa"}>
                    {event.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => openEdit(event)} className="p-2 text-white/30 hover:text-[#C5A059] transition-colors" title="Redigera"><Pencil size={16} /></button>
                  <button onClick={() => deleteEvent(event.slug)} className="p-2 text-white/30 hover:text-red-400 transition-colors" title="Radera"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
